import { useState, useEffect, createContext, useContext, useRef } from "react";
import { supabase } from "./supabase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        if (session) {
          loadEmployeeData(session.user);
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("getSession error:", err);
        setSession(null);
        setLoading(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session) {
        setSession(session);
        loadEmployeeData(session.user);
      } else if (event === "SIGNED_OUT") {
        setSession(null);
        setEmployee(null);
        setOrganizations([]);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function loadEmployeeData(user) {
    try {
      setError(null);

      // First try by auth_user_id
      let { data: empArray, error: empError } = await supabase
        .from("t4s_employees")
        .select(`*, organization:t4s_organizations!org_id(*)`)
        .eq("auth_user_id", user.id);

      let emp = empArray?.[0];

      // If not found, try by email
      if (!emp && !empError) {
        const { data: emailArray } = await supabase
          .from("t4s_employees")
          .select(`*, organization:t4s_organizations!org_id(*)`)
          .eq("email", user.email);

        emp = emailArray?.[0];
        if (emp) {
          // Link this auth user to the employee
          await supabase
            .from("t4s_employees")
            .update({ auth_user_id: user.id })
            .eq("id", emp.id);
        }
      }

      if (empError) {
        console.error("Employee load error:", empError);
        setError("Error loading employee data");
        setLoading(false);
        return;
      }

      if (!emp) {
        setError("No employee record found for this email.");
        setLoading(false);
        return;
      }

      setEmployee(emp);

      let orgs = [];

      // Admin users get access to ALL organizations
      if (emp.employee_type === "admin") {
        const { data: allOrgs, error: allOrgsError } = await supabase
          .from("t4s_organizations")
          .select("*")
          .order("name");

        if (allOrgsError) {
          console.error("Error loading all orgs for admin:", allOrgsError);
        } else if (allOrgs) {
          // Format to match expected structure, mark primary org
          orgs = allOrgs.map((org) => ({
            org_id: org.id,
            is_primary: org.id === emp.org_id,
            organization: org,
          }));
        }
      } else {
        // Non-admin: Load organizations from t4s_employee_orgs junction table
        const { data: empOrgs, error: orgsError } = await supabase
          .from("t4s_employee_orgs")
          .select(
            `
            org_id,
            is_primary,
            organization:t4s_organizations(*)
          `,
          )
          .eq("employee_id", emp.id);

        if (orgsError) {
          console.error("Error loading employee orgs:", orgsError);
        }

        orgs = empOrgs || [];
      }

      // If no orgs found, fall back to direct org_id
      if (orgs.length === 0 && emp.organization) {
        orgs = [
          {
            org_id: emp.org_id,
            is_primary: true,
            organization: emp.organization,
          },
        ];
      }

      setOrganizations(orgs);
      setLoading(false);
    } catch (err) {
      console.error("Error:", err);
      setError("Error loading user data");
      setLoading(false);
    }
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user,
        employee,
        organizations,
        loading,
        error,
        signOut,
        isAuthenticated: !!session && !!employee,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
